import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { formatDuration, formatDecimalHours, todayInPlant } from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';
import { Blad, Kpi, Ladowanie, Odznaka, Zmiana, type Ton } from '../ui/index.tsx';

/**
 * Pulpit odpowiada na jedno pytanie: co się dzieje teraz i czy jest problem.
 *
 * Świadomie NIE jest ścianą kart. Góra to stan dnia, środek to ruch w czasie
 * z porównaniem do poprzedniego okresu, dół to lista rzeczy wymagających decyzji.
 * Każdy wskaźnik prowadzi do danych — liczba, której nie da się sprawdzić,
 * nie ma tu czego szukać.
 */

interface Agregat {
  creditMin: number; availableMin: number; registeredMin: number; gapMin: number;
  efficiency: number | null; days: number;
  wpisow: number; ludzi: number; zlecen: number; sztuk: number; zakonczonych: number;
}
interface Problem { rodzaj: string; opis: string; waga: 'wysoka' | 'srednia'; link?: string }
interface Dane {
  dzien: string; plantTime: string;
  kpi: { dzien: Agregat; tydzien: Agregat; miesiac: Agregat; poprzedniMiesiac: Agregat };
  problemy: Problem[];
}

export default function Pulpit() {
  const [d, setD] = useState<Dane | null>(null);
  const [blad, setBlad] = useState<string | null>(null);
  const nav = useNavigate();

  const load = () => {
    setBlad(null);
    api.get<Dane>('/pulpit').then(setD).catch((e) => setBlad(e.message));
  };
  useEffect(load, []);
  // Ekran wisi na hali cały dzień — sam się odświeża co minutę.
  useEffect(() => { const t = setInterval(load, 60_000); return () => clearInterval(t); }, []);

  if (blad) return <Blad tresc={blad} ponow={load} />;
  if (!d) return <Ladowanie />;

  const proc = (v: number | null) => (v === null ? '—' : `${Math.round(v * 100)}%`);
  const ton = (v: number | null): Ton => (v === null ? 'neutral' : v >= 0.9 ? 'ok' : v >= 0.7 ? 'warn' : 'bad');
  const okres = (dni: number) => {
    const x = new Date(`${d.dzien}T00:00:00Z`);
    x.setUTCDate(x.getUTCDate() - dni + 1);
    return x.toISOString().slice(0, 10);
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-head-sm">Pulpit</h1>
        <span className="text-body-sm text-plum-muted">{d.dzien}</span>
        <span className="ml-auto text-caption text-plum-muted">
          dane na {d.plantTime} · czas zakładowy
        </span>
      </div>

      {/* DZIŚ — stan bieżący */}
      <section className="card p-5">
        <h2 className="text-label uppercase text-plum-muted mb-4">Dzisiaj</h2>
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-5">
          <Kpi etykieta="Efektywność" wartosc={proc(d.kpi.dzien.efficiency)} ton={ton(d.kpi.dzien.efficiency)}
               udzial={d.kpi.dzien.efficiency ?? 0}
               pod={`${formatDuration(d.kpi.dzien.creditMin)} normy`}
               onClick={() => nav(`/praca?od=${d.dzien}&do=${d.dzien}`)} />
          <Kpi etykieta="Wpisy" wartosc={d.kpi.dzien.wpisow}
               pod={`${policz(d.kpi.dzien.zakonczonych, 'zlecenie')} zamknięte`}
               onClick={() => nav(`/praca?od=${d.dzien}&do=${d.dzien}`)} />
          <Kpi etykieta="Pracownicy" wartosc={d.kpi.dzien.ludzi}
               pod="zarejestrowali pracę"
               onClick={() => nav(`/pracownicy?od=${d.dzien}&do=${d.dzien}`)} />
          <Kpi etykieta="Zlecenia" wartosc={d.kpi.dzien.zlecen}
               pod={`${policz(d.kpi.dzien.sztuk, 'sztuka')}`}
               onClick={() => nav('/zlecenia')} />
          <Kpi etykieta="Nierozpisany czas" wartosc={formatDuration(Math.max(0, d.kpi.dzien.gapMin))}
               ton={d.kpi.dzien.gapMin > 60 ? 'bad' : 'neutral'}
               pod={d.kpi.dzien.gapMin > 60 ? 'dni niedomknięte' : 'wszystko rozpisane'}
               onClick={() => nav(`/praca?od=${d.dzien}&do=${d.dzien}`)} />
        </div>
      </section>

      {/* RUCH W CZASIE */}
      <section className="card p-5">
        <h2 className="text-label uppercase text-plum-muted mb-4">Ruch w czasie</h2>
        <div className="grid sm:grid-cols-3 gap-5">
          {([
            ['Dzisiaj', d.kpi.dzien, 1],
            ['Ostatnie 7 dni', d.kpi.tydzien, 7],
            ['Ostatnie 30 dni', d.kpi.miesiac, 30],
          ] as Array<[string, Agregat, number]>).map(([tytul, a, dni]) => (
            <button key={tytul} type="button"
                    onClick={() => nav(`/praca?od=${okres(dni)}&do=${d.dzien}`)}
                    className="text-left rounded-lg border border-line p-4 transition-colors hover:bg-canvas">
              <div className="text-caption text-plum-muted">{tytul}</div>
              <div className={`kpi-sm mt-1.5 ${
                a.efficiency === null ? 'text-plum-muted'
                : a.efficiency >= 0.9 ? 'text-ok' : a.efficiency >= 0.7 ? 'text-warn' : 'text-bad'}`}>
                {proc(a.efficiency)}
              </div>
              <div className="meter mt-2.5">
                <span className="bg-plum" style={{ width: `${Math.min(100, (a.efficiency ?? 0) * 100)}%` }} />
              </div>
              <dl className="mt-3 grid grid-cols-2 gap-y-1 text-caption">
                <dt className="text-plum-muted">Norma</dt>
                <dd className="text-right tnum">{formatDecimalHours(a.creditMin)}</dd>
                <dt className="text-plum-muted">Dostępne</dt>
                <dd className="text-right tnum">{formatDecimalHours(a.availableMin)}</dd>
                <dt className="text-plum-muted">Sztuk</dt>
                <dd className="text-right tnum">{a.sztuk}</dd>
              </dl>
            </button>
          ))}
        </div>

        <p className="mt-4 pt-4 border-t border-line text-body-sm text-plum-soft">
          Ostatnie 30 dni wobec poprzednich 30:{' '}
          <b><Zmiana teraz={d.kpi.miesiac.efficiency} wczesniej={d.kpi.poprzedniMiesiac.efficiency} procent /></b>
          {' '}efektywności ({d.kpi.poprzedniMiesiac.efficiency !== null
            ? `${Math.round(d.kpi.poprzedniMiesiac.efficiency * 100)}% → ${proc(d.kpi.miesiac.efficiency)}`
            : 'brak danych z poprzedniego okresu'}).
        </p>
      </section>

      {/* PROBLEMY */}
      <section className="card overflow-hidden">
        <div className="px-5 py-4 border-b border-line flex items-center gap-3">
          <h2 className="text-label uppercase text-plum-muted">Wymaga decyzji</h2>
          {d.problemy.length === 0 && <Odznaka ton="ok">czysto</Odznaka>}
        </div>
        {d.problemy.length === 0 ? (
          <p className="px-5 py-6 text-body-sm text-plum-muted">
            Nic nie wymaga reakcji: każdy dzień jest rozpisany, wszystkie wpisy mają normę.
          </p>
        ) : (
          <ul className="divide-y divide-line">
            {d.problemy.map((p) => (
              <li key={p.rodzaj} className="px-5 py-4 flex items-start gap-3">
                <Odznaka ton={p.waga === 'wysoka' ? 'bad' : 'warn'}>
                  {p.waga === 'wysoka' ? 'wysoki' : 'średni'}
                </Odznaka>
                <p className="text-body-sm text-plum-soft flex-1">{p.opis}</p>
                {p.link && (
                  <button className="btn-quiet shrink-0" onClick={() => nav(p.link!)}>
                    Zobacz →
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>

      <p className="text-caption text-plum-muted">
        Każdy wskaźnik prowadzi do danych źródłowych. Jeśli liczba wygląda dziwnie, kliknij ją
        i zejdź do wpisów, z których powstała.
      </p>
    </div>
  );
}

export { todayInPlant };
